// "Go to" — jump the crew grid to a date or range (#588).
//
// The ask was a date control like the Concrete Pour Schedule's, for quicker
// navigation, so it wears the same bordered two-input shell (.psg-dates in
// qaqc/PourScheduleGrid.tsx) and reads as the same control. What it DOES is
// different, because this grid holds one fetched window rather than a list it
// can filter: the rules are in lib/gridJump.ts, and the page owns the window.

import { DATE_INPUT_MIN, DATE_INPUT_MAX } from '@/lib/dates';
import { MAX_WEEKS, isJumpableYmd } from './lib/gridJump';

const css = `
  .mp-jump { display: inline-flex; align-items: center; gap: 4px; margin-left: 12px; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 0 4px 0 10px; background: var(--ew-white); }
  .mp-jump-lbl { font: var(--ew-fw-semibold) 12px/1 var(--ew-font); color: var(--ew-n-500); white-space: nowrap; }
  .mp-jump input { border: none; font: var(--ew-fw-regular) 13px var(--ew-font); color: var(--ew-dark); background: transparent; padding: 5px 2px; width: 128px; }
  .mp-jump-x { font-size: 15px; line-height: 1; padding: 2px 6px; border: none; background: transparent; color: var(--ew-n-500); cursor: pointer; }
  .mp-jump-capped { font: var(--ew-fw-semibold) 11px var(--ew-font); color: var(--ew-safety-700); }
  /* On a phone the shell is wider than the viewport; let it take the row and
     wrap rather than scaling the whole page down. Same as the pour schedule. */
  @media (max-width: 560px) {
    .mp-jump { flex-wrap: wrap; width: 100%; margin-left: 0; }
    .mp-jump input { width: 118px; }
  }
`;

export default function GridJumpControl({ from, to, capped, onChange, onClear }: {
  /** The typed bounds, YYYY-MM-DD or ''. */
  from: string;
  to: string;
  /** The range asked for was longer than the grid holds — say so. */
  capped: boolean;
  onChange: (from: string, to: string) => void;
  onClear: () => void;
}) {
  return (
    <>
      <style>{css}</style>
      {/* From alone jumps; To alone ends the window there; both size it. */}
      <span className="mp-jump">
        <span className="mp-jump-lbl">Go to</span>
        <input
          type="date" aria-label="Go to date from"
          title="Jump the grid to the week of this date"
          min={DATE_INPUT_MIN} max={isJumpableYmd(to) ? to : DATE_INPUT_MAX}
          value={from} onChange={(e) => onChange(e.target.value, to)}
        />
        <span style={{ color: 'var(--ew-n-400)' }}>–</span>
        {/* Each bound limits the other only once it is a usable date: a year
            half-typed into one field (0202-…) must not grey out the other
            field's picker. */}
        <input
          type="date" aria-label="Go to date to"
          title="Show through the week of this date"
          min={isJumpableYmd(from) ? from : DATE_INPUT_MIN} max={DATE_INPUT_MAX}
          value={to} onChange={(e) => onChange(from, e.target.value)}
        />
        {(from || to) && (
          <button
            type="button" className="mp-jump-x"
            title="Clear the dates (the grid stays where it is)" aria-label="Clear the dates"
            onClick={onClear}
          >
            ×
          </button>
        )}
      </span>
      {/* A range that came back shorter than asked must say so, or it reads as
          the control ignoring the To date. */}
      {capped && (
        <span role="status" className="mp-jump-capped">
          Showing the first {MAX_WEEKS} weeks — the longest the grid holds
        </span>
      )}
    </>
  );
}
